import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Animated, AppState, Easing, Platform, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import {
    buildMessages,
    FALLBACK_SECTIONS,
    TOPICS,
    type Cat,
    type Message
} from "./liveActivityMessages";
import { usePulseValue } from "./useAnimationHelpers";

import { colors, fonts, iconTints, radius, shadows } from "@/lib/theme";

const USE_NATIVE = Platform.OS !== "web";

function useAppStateActive(): boolean {
    const [active, setActive] = useState(() => AppState.currentState === "active");
    useEffect(() => {
        const sub = AppState.addEventListener("change", (state) => {
            setActive(state === "active");
        });
        return () => sub.remove();
    }, []);
    return active;
}

function shuffle<T>(arr: T[]): T[] {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

const ICON_NAMES: Record<Cat, keyof typeof MaterialCommunityIcons.glyphMap> = {
    read: "file-document-outline",
    who: "account-heart-outline",
    match: "shape-outline",
    write: "pencil-outline",
    level: "text-short",
    llm: "auto-fix",
    check: "shield-check-outline",
    persist: "database-outline",
    done: "check-circle-outline"
};

const ICON_TINTS: Record<Cat, { bg: string; fg: string }> = {
    read: iconTints.teal,
    who: iconTints.pink,
    match: iconTints.amber,
    write: iconTints.purple,
    level: iconTints.sky,
    llm: iconTints.cyan,
    check: iconTints.tealMuted,
    persist: iconTints.orange,
    done: iconTints.tealStrong
};

interface Row {
    id: number;
    message: Message;
}

const MAX_VISIBLE = 6;
const TICK_MS = 900;

function formatTime(seconds: number): string {
    const mm = Math.floor(seconds / 60);
    const ss = (seconds % 60).toString().padStart(2, "0");
    return `${mm}:${ss}`;
}

export interface LiveActivityContent {
    /**
     * The visit's OWN section names, in composition order. Pass them — the
     * fallback is the standard assessment's list and is wrong on every special
     * visit. Empty or omitted falls back.
     */
    sections?: string[];
    /** Teaching topics to narrate. Omitted uses the generic list. */
    topics?: string[];
    /** Real answer count for this visit, so the panel stops claiming 236. */
    answerCount?: number;
}

export function TeachingLiveActivityStream({
    sections,
    topics,
    answerCount
}: LiveActivityContent = {}) {
    const [rows, setRows] = useState<Row[]>([]);
    const [seconds, setSeconds] = useState(0);
    const liveDotOpacity = usePulseValue(1200, 0.4, 1);
    const active = useAppStateActive();

    const deckRef = useRef<Message[]>([]);
    const deckIdxRef = useRef(0);
    const nextIdRef = useRef(0);
    const hasInitialFillRef = useRef(false);

    const deck = useMemo(
        () =>
            buildMessages(
                sections && sections.length > 0 ? sections : FALLBACK_SECTIONS,
                topics && topics.length > 0 ? topics : TOPICS,
                answerCount
            ),
        [sections, topics, answerCount]
    );

    const drawNext = useCallback((): Message => {
        if (deckRef.current.length === 0) {
            deckRef.current = shuffle(deck);
        }
        const m = deckRef.current[deckIdxRef.current % deckRef.current.length];
        deckIdxRef.current += 1;
        return m;
    }, [deck]);

    useEffect(() => {
        if (!hasInitialFillRef.current) {
            const initial: Row[] = [];
            for (let i = 0; i < 4; i++) {
                initial.push({ id: nextIdRef.current++, message: drawNext() });
            }
            setRows(initial);
            hasInitialFillRef.current = true;
        }
        // Same contract as the POC stream: don't tick while backgrounded. The
        // seconds counter therefore shows ELAPSED ACTIVE TIME (decorative);
        // the phase pill in TeachingGeneratingAnimation is the wall-clock one.
        if (!active) {
            return;
        }
        const tickHandle = setInterval(() => {
            setRows((prev) => {
                const next: Row = { id: nextIdRef.current++, message: drawNext() };
                const updated = [...prev, next];
                while (updated.length > MAX_VISIBLE) {
                    updated.shift();
                }
                return updated;
            });
        }, TICK_MS);

        const timerHandle = setInterval(() => {
            setSeconds((s) => s + 1);
        }, 1000);

        return () => {
            clearInterval(tickHandle);
            clearInterval(timerHandle);
        };
    }, [active, drawNext]);

    return (
        <View style={styles.container}>
            <View style={styles.header}>
                <Animated.View style={[styles.liveDot, { opacity: liveDotOpacity }]} />
                <Text style={styles.label}>Live activity</Text>
                <Text style={styles.timer}>{formatTime(seconds)}</Text>
            </View>

            <View style={styles.streamArea}>
                {rows.map((row) => (
                    <StreamRow key={row.id} message={row.message} />
                ))}

                <LinearGradient
                    pointerEvents="none"
                    colors={[colors.surface, "rgba(255,255,255,0)"]}
                    style={styles.fadeTop}
                />
                <LinearGradient
                    pointerEvents="none"
                    colors={["rgba(255,255,255,0)", colors.surface]}
                    style={styles.fadeBottom}
                />
            </View>
        </View>
    );
}

interface StreamRowProps {
    message: Message;
}

function StreamRow({ message }: StreamRowProps) {
    const opacity = useRef(new Animated.Value(0)).current;
    const translateY = useRef(new Animated.Value(18)).current;

    useEffect(() => {
        Animated.parallel([
            Animated.timing(opacity, {
                toValue: 1,
                duration: 480,
                easing: Easing.out(Easing.cubic),
                useNativeDriver: USE_NATIVE
            }),
            Animated.timing(translateY, {
                toValue: 0,
                duration: 480,
                easing: Easing.out(Easing.cubic),
                useNativeDriver: USE_NATIVE
            })
        ]).start();
    }, [opacity, translateY]);

    const tint = ICON_TINTS[message.cat];

    return (
        <Animated.View style={[styles.row, { opacity, transform: [{ translateY }] }]}>
            <View style={[styles.rowIcon, { backgroundColor: tint.bg }]}>
                <MaterialCommunityIcons name={ICON_NAMES[message.cat]} size={14} color={tint.fg} />
            </View>
            <Text style={styles.rowText} numberOfLines={1} ellipsizeMode="tail">
                {renderAccents(message.text)}
            </Text>
        </Animated.View>
    );
}

function renderAccents(text: string) {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) {
            return (
                <Text key={i} style={styles.accent}>
                    {part.slice(2, -2)}
                </Text>
            );
        }
        return <Text key={i}>{part}</Text>;
    });
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        marginHorizontal: 16,
        marginTop: 12,
        marginBottom: 16,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.lg,
        overflow: "hidden",
        ...shadows.sm
    },
    header: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        paddingHorizontal: 18,
        paddingVertical: 13,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    liveDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: colors.teal400
    },
    label: {
        flex: 1,
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.textPrimary,
        letterSpacing: -0.1
    },
    timer: {
        fontSize: 11,
        fontFamily: fonts.semiBold,
        color: colors.textTertiary,
        fontVariant: ["tabular-nums"]
    },
    streamArea: {
        flex: 1,
        paddingVertical: 8,
        position: "relative"
    },
    row: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 12,
        paddingHorizontal: 18,
        paddingVertical: 8
    },
    rowIcon: {
        width: 26,
        height: 26,
        borderRadius: 8,
        alignItems: "center",
        justifyContent: "center",
        marginTop: 1
    },
    rowText: {
        flex: 1,
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        lineHeight: 18,
        letterSpacing: -0.1
    },
    accent: {
        color: colors.teal600,
        fontFamily: fonts.bold
    },
    fadeTop: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 22
    },
    fadeBottom: {
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: 22
    }
});
